import { useCallback, useEffect, useMemo, useState } from "react";
import {
  hasGoogleSheetsScope,
  SheetsReauthorizeRequiredError,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { useConnectionsStore, selectByType } from "stores/connectionsStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  useAddGoogleConnection,
  useAuthorizeSheets,
  type GoogleOauthOutcome,
} from "components/main/connections/useGoogleOauthPopup";
import {
  exportToGoogleSheet,
  type ExportSourceWire,
  type ExportToSheetResult,
  type SheetExportDestination,
} from "./exportApi";

export type ExportPhase = "idle" | "exporting" | "success" | "error";

/// Orchestrates the export: resolves a Sheets-scoped Google connection (or
/// connects one), then forwards the chosen destination to the server.
export function useExportToGoogleSheet(source: ExportSourceWire) {
  const list = useConnectionsStore((s) => s.list);
  const fetchIfStale = useConnectionsStore((s) => s.fetchIfStale);
  const refetch = useConnectionsStore((s) => s.refetch);
  const { start: startConnect, pending: connecting } = useAddGoogleConnection();
  const { authorize: authorizeSheetsOn, pending: authorizing } =
    useAuthorizeSheets();

  useEffect(() => {
    void fetchIfStale();
  }, [fetchIfStale]);

  // Every Google account the user has connected (in Connections), regardless of
  // scope — same source the Connections settings show. Accounts without the
  // Sheets scope can have it added in place via `authorizeSheets` (incremental
  // auth, no new account, same drive.file scope import uses).
  const connections = useMemo<SavedConnection[]>(
    () => selectByType(list, "google_oauth"),
    [list],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Selected connection, defaulting to the first available.
  const connection = useMemo<SavedConnection | null>(
    () =>
      connections.find((c) => c.id === selectedId) ?? connections[0] ?? null,
    [connections, selectedId],
  );

  // Does the selected account already grant the Sheets (drive.file) scope?
  const connectionHasSheets =
    connection?.credentials.type === "google_oauth" &&
    hasGoogleSheetsScope(connection.credentials);

  const [phase, setPhase] = useState<ExportPhase>("idle");
  const [result, setResult] = useState<ExportToSheetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set when Google rejects the stored refresh token (401 reauthorize_required),
  // from either the file-picker token fetch or the export call itself.
  const [needsReauth, setNeedsReauth] = useState(false);

  // A dead refresh token belongs to one account — switching clears the flag.
  useEffect(() => {
    setNeedsReauth(false);
  }, [connection?.id]);

  const flagReauthNeeded = useCallback(() => setNeedsReauth(true), []);

  const connect = useCallback(async (): Promise<GoogleOauthOutcome> => {
    const outcome = await startConnect({ clientType: "sheets" });
    if (outcome.status === "ok") {
      await refetch();
      setSelectedId(outcome.saved_connection_id);
    }
    return outcome;
  }, [startConnect, refetch]);

  // Reauthorize the selected account in place (incremental auth, same
  // drive.file scope import uses) — clears both the missing-scope and the
  // dead-refresh-token cases. On success drop any stale export error.
  const authorizeSheets =
    useCallback(async (): Promise<GoogleOauthOutcome | null> => {
      if (!connection) return null;
      const outcome = await authorizeSheetsOn(connection.id);
      if (outcome.status === "ok") {
        setNeedsReauth(false);
        setError(null);
        setPhase("idle");
      }
      return outcome;
    }, [authorizeSheetsOn, connection]);

  const runExport = useCallback(
    async (destination: SheetExportDestination) => {
      if (!connection) {
        setError("Connect a Google account first.");
        setPhase("error");
        return;
      }
      setPhase("exporting");
      setError(null);
      // Progress and errors stay in the (still-open) dialog so a failed
      // reauthorize can be retried in place. Only the success confirmation —
      // with the Open link — surfaces as a global toast, like upload completion.
      try {
        const r = await exportToGoogleSheet({
          source,
          savedConnectionId: connection.id,
          destination,
        });
        setResult(r);
        setPhase("success");
        showNotificationGlobal(
          `Exported ${r.rows_written.toLocaleString()} rows to ${r.sheet_title}`,
          "success",
          {
            label: "Open",
            onClick: () =>
              window.open(r.spreadsheet_url, "_blank", "noopener,noreferrer"),
          },
        );
      } catch (e) {
        setPhase("error");
        if (e instanceof SheetsReauthorizeRequiredError) {
          setNeedsReauth(true);
          setError(
            "Google Sheets access expired — reauthorize below to retry.",
          );
        } else {
          setError(e instanceof Error ? e.message : "Export failed");
        }
      }
    },
    [connection, source],
  );

  const reset = useCallback(() => {
    setPhase("idle");
    setResult(null);
    setError(null);
    setNeedsReauth(false);
  }, []);

  return {
    connections,
    connection,
    connectionHasSheets,
    selectConnection: setSelectedId,
    connecting,
    connect,
    authorizeSheets,
    authorizing,
    needsReauth,
    flagReauthNeeded,
    phase,
    result,
    error,
    runExport,
    reset,
  };
}
